/**
 * platform-invoices-page.tsx — Platform → Hoá đơn điện tử (super-admin). Route `/platform/invoices`.
 *
 * Hoá đơn SePay eInvoice của đơn mua gói mọi tổ chức. Lọc theo trạng thái, tổ chức, khoảng ngày
 * đặt, từ khoá (số hoá đơn / mã đơn / tên tổ chức). Hoá đơn lỗi / còn chờ → "Thử lại"; lỗi SePay
 * "chưa xác nhận được kết quả" → phải liên hệ SePay trước, thử lại với "Bỏ qua cảnh báo".
 */
import { useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { AlertTriangle, Download, Loader2, RefreshCw, Search } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { ErrorState } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { usePlatformOrgs } from '@/hooks/use-platform'
import {
  downloadPlatformInvoice, useEInvoiceConfig, usePlatformInvoices, useRetryInvoice, type PlatformInvoiceRow,
} from '@/hooks/use-platform-invoices'
import { apiError } from '@/lib/api-client'
import { formatVnd } from '@/lib/format-money'
import { INVOICE_STATUS } from '@/pages/billing/invoice-info'

const PAGE_SIZE = 50
const STATUS_FILTERS = ['pending', 'issued', 'failed', 'cancelled'] as const

export function PlatformInvoicesPage() {
  const [status, setStatus] = useState('')
  const [orgId, setOrgId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [q, setQ] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = usePlatformInvoices({
    status: status || undefined, orgId: orgId || undefined, from: from || undefined, to: to || undefined,
    q: search || undefined, page, limit: PAGE_SIZE,
  })
  const orgs = usePlatformOrgs({ pageSize: 200 })
  const config = useEInvoiceConfig()
  const retry = useRetryInvoice()

  const doRetry = (row: PlatformInvoiceRow) => {
    let force = false
    if (row.invoice.unconfirmed) {
      force = window.confirm(
        'SePay chưa xác nhận kết quả lần phát hành trước. Chỉ tiếp tục khi SePay đã xác nhận CHƯA có hoá đơn — gửi lại có thể tạo hoá đơn trùng. Tiếp tục?',
      )
      if (!force) return
    }
    retry.mutate(
      { orderId: row.orderId, force },
      { onSuccess: () => toast.success('Đã đưa hoá đơn vào hàng đợi xuất lại'), onError: (err) => toast.error(apiError(err)) },
    )
  }

  const download = async (row: PlatformInvoiceRow, type: 'pdf' | 'xml') => {
    try {
      await downloadPlatformInvoice(row.orderId, type)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const columns: Column<PlatformInvoiceRow>[] = [
    {
      key: 'created', header: 'Ngày đặt',
      cell: (r) => <span className="whitespace-nowrap text-sm text-muted-foreground">{dayjs(r.createdAt).format('DD/MM/YYYY HH:mm')}</span>,
    },
    {
      key: 'org', header: 'Tổ chức',
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{r.org?.name ?? '—'}</p>
          <p className="truncate text-xs text-muted-foreground">{r.planName} · <span className="font-mono">{r.paymentCode ?? r.orderId.slice(0, 8)}</span></p>
        </div>
      ),
    },
    {
      key: 'buyer', header: 'Người mua',
      cell: (r) => {
        const i = r.invoice.info
        if (!i) return <span className="text-muted-foreground">—</span>
        return (
          <div className="min-w-0 max-w-[240px] text-sm">
            <p className="truncate">{i.buyerType === 'company' ? i.companyName : i.buyerName}</p>
            <p className="truncate text-xs text-muted-foreground">{[i.taxCode && `MST ${i.taxCode}`, i.emails[0]].filter(Boolean).join(' · ')}</p>
          </div>
        )
      },
    },
    { key: 'amount', header: 'Số tiền', align: 'right', cell: (r) => <span className="tabular-nums">{formatVnd(r.amount)}</span> },
    {
      key: 'status', header: 'Hoá đơn',
      cell: (r) => {
        const s = INVOICE_STATUS[r.invoice.status] ?? INVOICE_STATUS.pending
        return (
          <div className="flex min-w-0 max-w-[280px] flex-col items-start gap-1">
            <span className="flex items-center gap-1.5">
              <Badge variant={s.variant}>{r.invoice.processing ? 'SePay đang xử lý' : s.text}</Badge>
              {r.invoice.invoiceNo && <span className="font-mono text-xs">Số {r.invoice.invoiceNo}</span>}
              {r.orderStatus !== 'paid' && <span className="text-xs text-muted-foreground">(chờ thanh toán)</span>}
            </span>
            {r.invoice.error && (
              <span className="line-clamp-2 text-xs text-destructive" title={r.invoice.error}>
                {r.invoice.unconfirmed && <AlertTriangle className="mr-1 inline h-3 w-3" />}{r.invoice.error}
              </span>
            )}
            {r.invoice.issuedAt && <span className="text-xs text-muted-foreground">Phát hành {dayjs(r.invoice.issuedAt).format('DD/MM/YYYY')}</span>}
          </div>
        )
      },
    },
    {
      key: 'actions', header: '', align: 'right',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          {r.invoice.status === 'issued' && (
            <>
              <Button size="sm" variant="ghost" onClick={() => download(r, 'pdf')}><Download className="h-3.5 w-3.5" /> PDF</Button>
              {r.invoice.xmlUrl && <Button size="sm" variant="ghost" onClick={() => download(r, 'xml')}>XML</Button>}
            </>
          )}
          {(r.invoice.status === 'failed' || r.invoice.status === 'pending') && r.orderStatus === 'paid' && (
            <Button size="sm" variant="outline" disabled={retry.isPending} onClick={() => doRetry(r)}>
              {retry.isPending && retry.variables?.orderId === r.orderId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {r.invoice.status === 'failed' ? 'Thử lại' : 'Đẩy lại'}
            </Button>
          )}
        </div>
      ),
    },
  ]

  const counts = data?.counts ?? {}
  const cfg = config.data

  return (
    <div className="space-y-4">
      <PageHeader
        title="Hoá đơn điện tử"
        description="Hoá đơn SePay eInvoice của các đơn mua gói. Hoá đơn được phát hành tự động sau khi đơn thanh toán thành công."
      />

      {cfg && !cfg.configured && (
        <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <span>
            Chưa cấu hình SePay eInvoice — thiếu <code>{cfg.missing.join(', ')}</code> trong .env của máy chủ.
            Đơn vẫn thanh toán bình thường; hoá đơn giữ trạng thái "Đang xuất" và tự phát hành khi cấu hình xong.
          </span>
        </div>
      )}
      {cfg?.configured && (
        <p className="text-xs text-muted-foreground">
          Môi trường <b>{cfg.env === 'production' ? 'Production (hoá đơn thật)' : 'Sandbox'}</b> · mẫu {cfg.templateCode}
          {cfg.invoiceSeries ? ` · ký hiệu ${cfg.invoiceSeries}` : ' · ký hiệu lấy từ SePay'} · VAT {cfg.vatRate < 0 ? (cfg.vatRate === -2 ? 'không chịu thuế' : 'không kê khai') : `${cfg.vatRate}%`}
          {cfg.priceIncludesVat ? ' (giá đã gồm VAT)' : ' (cộng VAT trên số đã thu)'}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {['', ...STATUS_FILTERS].map((s) => (
          <Button key={s || 'all'} size="sm" variant={status === s ? 'default' : 'outline'} onClick={() => { setStatus(s); setPage(1) }}>
            {s ? INVOICE_STATUS[s].text : 'Tất cả'}
            {s && counts[s as keyof typeof counts] ? <span className="ml-1 tabular-nums opacity-70">{counts[s as keyof typeof counts]}</span> : null}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <form
          className="relative"
          onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); setPage(1) }}
        >
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Số hoá đơn, mã đơn, tổ chức…" className="w-64 pl-8" />
        </form>
        <Select value={orgId || 'all'} onValueChange={(v) => { setOrgId(v === 'all' ? '' : v); setPage(1) }}>
          <SelectTrigger className="w-60"><SelectValue placeholder="Tổ chức" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả tổ chức</SelectItem>
            {(orgs.data?.items ?? []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
          Từ <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1) }} className="w-40" />
        </label>
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
          đến <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1) }} className="w-40" />
        </label>
        {data && <span className="text-sm text-muted-foreground">{data.total} hoá đơn</span>}
      </div>

      {isError ? (
        <ErrorState />
      ) : (
        <DataTable columns={columns} rows={data?.items ?? []} rowKey={(r) => r.orderId} loading={isLoading} emptyTitle="Chưa có hoá đơn nào" />
      )}

      {data && data.total > PAGE_SIZE && <Pagination page={page} limit={PAGE_SIZE} total={data.total} onPageChange={setPage} />}
    </div>
  )
}
